import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  PagedResult,
  CitizenCaseListItem,
  CitizenDashboard,
} from '../models/case.model';
import { CaseDraft, UpdateCaseAiAnalysisCommand } from '../models/filing.model';
import { ApiResponse } from '../models/api-response.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CaseService {
  constructor(private http: HttpClient) {}

  /** GET /api/Citizen/cases — the current citizen's cases (paginated; optional CaseFilingStatus filter) */
  getCases(
    pageNumber = 1,
    pageSize = 10,
    search = '',
    status?: number,
  ): Observable<PagedResult<CitizenCaseListItem>> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);
    if (search) params = params.set('Search', search);
    if (status != null) params = params.set('Status', status);
    return this.http
      .get<
        ApiResponse<PagedResult<CitizenCaseListItem>>
      >(`${environment.apiUrl}/Citizen/cases`, { params })
      .pipe(map((res) => res.data));
  }

  /** GET /api/Citizen/dashboard — citizen dashboard summary + recent cases */
  getDashboard(): Observable<CitizenDashboard> {
    return this.http
      .get<
        ApiResponse<CitizenDashboard>
      >(`${environment.apiUrl}/Citizen/dashboard`)
      .pipe(map((res) => res.data));
  }

  /** GET /api/Cases/{id} — full case detail */
  getCase(id: string): Observable<CaseDraft> {
    return this.http
      .get<ApiResponse<CaseDraft>>(`${environment.apiUrl}/Cases/${id}`)
      .pipe(map((res) => res.data));
  }

  /** POST /api/Cases/{id}/corrections (multipart) — upload a missing/corrected document */
  submitCorrection(
    caseFilingId: string,
    documentType: string,
    file: File,
  ): Observable<ApiResponse<unknown>> {
    const form = new FormData();
    form.append('DocumentType', documentType);
    form.append('File', file);
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Cases/${caseFilingId}/corrections`,
      form,
    );
  }

  /** PATCH /api/Cases/{id}/ai-analysis — persist AI results and update case status as needed */
  updateAiAnalysis(
    caseFilingId: string,
    command: UpdateCaseAiAnalysisCommand,
  ): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(
      `${environment.apiUrl}/Cases/${caseFilingId}/ai-analysis`,
      command,
    );
  }

  /** POST /api/Cases/{id}/submit — resubmit the case after corrections */
  submitCase(
    caseFilingId: string,
    paymentRequestId: string,
    confirmationReference: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Cases/${caseFilingId}/submit`,
      { paymentRequestId, confirmationReference },
    );
  }
}
